import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/Primitives";

const ymIdx = (s) => { const m = /^(\d{4})-(\d{2})/.exec(s || ""); return m ? +m[1] * 12 + (+m[2] - 1) : null; };
const nowIdx = () => { const d = new Date(); return d.getFullYear() * 12 + d.getMonth(); };
const fmtYm = (i) => `${String(Math.floor(i / 12))}-${String((i % 12) + 1).padStart(2, "0")}`;
const MAX_GAP = 3;

export const findGaps = (education, work) => {
  const spans = [
    ...education.filter((e) => ymIdx(e.start) != null || ymIdx(e.end) != null).map((e) => ({ kind: "edu", label: e.school || "Pendidikan", s: ymIdx(e.start), e: ymIdx(e.end) })),
    ...work.filter((w) => ymIdx(w.start) != null).map((w) => ({ kind: "work", label: w.company || "Pekerjaan", s: ymIdx(w.start), e: w.end ? ymIdx(w.end) : nowIdx() })),
  ].filter((x) => x.e != null).sort((a, b) => (a.e ?? 0) - (b.e ?? 0));
  const gaps = [];
  for (let i = 1; i < spans.length; i++) {
    const prev = spans[i - 1], cur = spans[i];
    if (cur.s == null) continue;
    const g = cur.s - prev.e - 1;
    if (g > MAX_GAP) gaps.push({ from: fmtYm(prev.e + 1), to: fmtYm(cur.s - 1), months: g, after: prev, before: cur });
  }
  const last = spans[spans.length - 1];
  if (last && last.e != null && nowIdx() - last.e - 1 > MAX_GAP) gaps.push({ from: fmtYm(last.e + 1), to: "sekarang", months: nowIdx() - last.e - 1, after: last, before: null });
  return gaps;
};

export const GapNotices = ({ education, work }) => {
  const gaps = findGaps(education, work);
  if (!gaps.length) return null;
  return (
    <div className="sm:col-span-2 space-y-1.5" data-testid="history-gap-notices">
      {gaps.map((g, i) => <div key={i} data-testid={`history-gap-${i}`} className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
        <span>Ada celah waktu <b>{g.months} bulan</b> ({g.from} s/d {g.to}) setelah {g.after.kind === "edu" ? "lulus dari" : "bekerja di"} <b>{g.after.label}</b>{g.before ? ` sebelum ${g.before.kind === "edu" ? "masuk" : "bekerja di"} ${g.before.label}` : ""}. Mohon tambahkan informasi pekerjaan/kegiatan pada periode tersebut (maksimal celah {MAX_GAP} bulan).</span>
      </div>)}
    </div>
  );
};

const Row = ({ children, onRemove, testId }) => (
  <div className="rounded-lg border border-border p-3 grid sm:grid-cols-12 gap-2 relative" data-testid={testId}>
    {children}
    <Button type="button" size="icon" variant="ghost" className="absolute -top-2 -right-2 h-7 w-7 bg-background border border-border text-destructive hover:text-destructive" onClick={onRemove} data-testid={`${testId}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
  </div>
);

export const EducationHistory = ({ items, onChange }) => {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="sm:col-span-2" data-testid="education-history">
      <Field label="Riwayat Pendidikan" hint="Urutkan dari yang terlama. Format bulan: YYYY-MM">
        <div className="space-y-2 pt-1">
          {items.map((e, i) => <Row key={i} testId={`edu-row-${i}`} onRemove={() => onChange(items.filter((_, j) => j !== i))}>
            <Input className="sm:col-span-4" placeholder="Nama sekolah" value={e.school || ""} onChange={(ev) => upd(i, "school", ev.target.value)} data-testid={`edu-school-${i}`} />
            <Input className="sm:col-span-2" placeholder="Jenjang (SMK/S1)" value={e.level || ""} onChange={(ev) => upd(i, "level", ev.target.value)} data-testid={`edu-level-${i}`} />
            <Input className="sm:col-span-2" placeholder="Jurusan" value={e.major || ""} onChange={(ev) => upd(i, "major", ev.target.value)} data-testid={`edu-major-${i}`} />
            <Input className="sm:col-span-2" type="month" value={e.start || ""} onChange={(ev) => upd(i, "start", ev.target.value)} data-testid={`edu-start-${i}`} />
            <Input className="sm:col-span-2" type="month" value={e.end || ""} onChange={(ev) => upd(i, "end", ev.target.value)} data-testid={`edu-end-${i}`} />
          </Row>)}
          <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { school: "", level: "", major: "", start: "", end: "" }])} data-testid="edu-add"><Plus className="h-3.5 w-3.5 mr-1" />Tambah pendidikan</Button>
        </div>
      </Field>
    </div>
  );
};

export const WorkHistory = ({ items, onChange }) => {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="sm:col-span-2" data-testid="work-history">
      <Field label="Pengalaman Kerja" hint="Kosongkan 'Selesai' jika masih bekerja. Celah antar pekerjaan lebih dari 3 bulan akan ditandai.">
        <div className="space-y-2 pt-1">
          {items.map((w, i) => <Row key={i} testId={`work-row-${i}`} onRemove={() => onChange(items.filter((_, j) => j !== i))}>
            <Input className="sm:col-span-4" placeholder="Perusahaan" value={w.company || ""} onChange={(ev) => upd(i, "company", ev.target.value)} data-testid={`work-company-${i}`} />
            <Input className="sm:col-span-4" placeholder="Posisi / jabatan" value={w.position || ""} onChange={(ev) => upd(i, "position", ev.target.value)} data-testid={`work-position-${i}`} />
            <Input className="sm:col-span-2" type="month" value={w.start || ""} onChange={(ev) => upd(i, "start", ev.target.value)} data-testid={`work-start-${i}`} />
            <Input className="sm:col-span-2" type="month" placeholder="Selesai" value={w.end || ""} onChange={(ev) => upd(i, "end", ev.target.value)} data-testid={`work-end-${i}`} />
            <Input className="sm:col-span-12" placeholder="Uraian singkat tugas" value={w.description || ""} onChange={(ev) => upd(i, "description", ev.target.value)} data-testid={`work-desc-${i}`} />
          </Row>)}
          <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { company: "", position: "", start: "", end: "", description: "" }])} data-testid="work-add"><Plus className="h-3.5 w-3.5 mr-1" />Tambah pengalaman</Button>
        </div>
      </Field>
    </div>
  );
};
